<template>
  <div
    ref="siteSearchOverlay"
    id="site-search-overlay"
    class="js-site-search-overlay site-search-overlay"
  >
    <div class="section-container">
      <div class="section-layout">
        <div class="site-search">
          <header class="site-header-container">
            <button
              v-search-toggle
              aria-expanded="true"
              aria-controls="site-search-overlay"
              aria-label="Toggle search overlay"
              class="site-menu-toggle dark no-transition"
              style="margin-left: auto;"
            >
              <Hamburger />
            </button>
          </header>
          <div
            :class="{ 'is-hidden': hasPerformedSearched }"
            class="site-search-title new-heading-2"
          >{{ dictionary.title }}</div>
          <SearchBar
            ref="searchBar"
            v-model="searchBarValue"
            :placeholder="dictionary.searchBarPlaceholder"
            :clear-button-label="dictionary.searchBarClearButton"
            @focus="scrollToTopOfOverlay"
            @clear="onSearchBarClear"
          />
          <div class="site-search-result">
            <h2
              v-if="results && results.length && !isLoading"
              class="site-search-result-title new-heading-2 wrap-word"
            >{{ resultsFoundLabel }}</h2>
            <template v-if="isLoading">
              <ul class="result-list list-unstyled">
                <ResultCardSkeleton />
                <ResultCardSkeleton />
                <ResultCardSkeleton />
              </ul>
            </template>
            <template v-else-if="results">
              <p
                v-if="!results.length"
                class="site-search-no-results text-lead m-0"
              >{{ dictionary.noResultsFound }}</p>
              <div v-else>
                <ul class="result-list list-unstyled">
                  <li v-for="(result, index) in results" :key="result.url">
                    <a :href="result.url" class="link-unstyled" @click="trackSearchResult(result.title, index + 1)">
                      <ResultCard
                        as="article"
                        :title="result.title"
                        :subtitle="result.customTag"
                        :description="result.description"
                        :image-source="result.imageSource"
                        :image-alt="result.imageAlt"
                      />
                    </a>
                  </li>
                </ul>
              </div>
            </template>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import {
  onMounted,
  ref,
  watch,
  computed,
} from 'vue';
import debounce from 'lodash.debounce';
import SearchBar from '@/components/SearchBar.vue';
import Hamburger from '@/components/Hamburger.vue';
import ResultCard from '@/components/ResultCard.vue';
import ResultCardSkeleton from '@/components/ResultCardSkeleton.vue';
import { searchToggle as vSearchToggle } from '@/directives';
import { SearchService } from '@/data/services/search';
import { sendAnalyticsEvent } from '@/utils/analytics';

const props = defineProps({
  dictionary: {
    type: Object,
    default: () => ({}),
  },
});

const siteSearchOverlay = ref(null);
const searchBar = ref(null);
const searchBarIsSticky = ref(false);
const searchBarValue = ref('');
const hasPerformedSearched = ref(false);
const lastUsedSearchTerm = ref('');
const results = ref(null);
const isLoading = ref(false);

const resultsFoundLabel = computed(() => props.dictionary.resultsFound
  .replace('{numberOfResults}', results.value?.length)
  .replace('{searchTerm}', lastUsedSearchTerm.value));

onMounted(() => {
  siteSearchOverlay.value.addEventListener('searchstatechange', ({ detail }) => {
    if (detail.isSearchExpanded) {
      // auto focus search bar when search overlay is expanded
      searchBar.value.focusInput();
    } else {
      // reset search when search overlay is closed
      resetSearch();
      hasPerformedSearched.value = false;
    }
  });

  measureSearchBarStickiness();
});

// initially hide the title without typing delay (debounce)
watch(searchBarValue, (newSearchBarValue) => {
  if (newSearchBarValue) {
    hasPerformedSearched.value = true;
  }
});

// fetch search results while the user is typing with delay (debounce) (so not on keyboard Enter)
watch(searchBarValue, debounce((newSearchBarValue) => {
  if (!newSearchBarValue) {
    return;
  }

  lastUsedSearchTerm.value = newSearchBarValue;
  isLoading.value = true;

  SearchService.getSearchResults(newSearchBarValue)
    .then((result) => {
      results.value = result;
      trackSearch();
    })['catch'](() => {
      results.value = [];
    })['finally'](() => {
      isLoading.value = false;
    });
}, 500));

watch(searchBarIsSticky, (isSticky) => {
  if (isSticky) {
    searchBar.value.blurInput();
  }
});

async function onSearchBarClear() {
  const isSearchOverlayScrolled = siteSearchOverlay.value.scrollTop > 0;
  if (isSearchOverlayScrolled) {
    // scroll to top before resetting search
    setupScrollEndListener().then(resetSearch);
    scrollToTopOfOverlay();
  } else {
    resetSearch();
  }
}

function resetSearch() {
  results.value = null;
  searchBarValue.value = '';
}

function scrollToTopOfOverlay() {
  siteSearchOverlay.value.scroll({
    top: 0,
    behavior: 'smooth',
  });
}

function setupScrollEndListener() {
  return new Promise((resolve) => {
    const onScroll = debounce(() => {
      siteSearchOverlay.value.removeEventListener('scroll', onScroll);
      resolve();
    }, 150);

    siteSearchOverlay.value.addEventListener('scroll', onScroll, { passive: true });
  });
}

function measureSearchBarStickiness() {
  const observer = new IntersectionObserver(([entry]) => {
    searchBarIsSticky.value = entry.intersectionRatio < 1;
  }, { threshold: [1] });
  observer.observe(searchBar.value.root);
}

// analytics
function trackSearch() {
  sendAnalyticsEvent('search', {
    searchQuery: searchBarValue.value,
    totalResults: results.value?.length || 0,
  });
}

function trackSearchResult(title, position) {
  sendAnalyticsEvent('searchResults', {
    searchQuery: searchBarValue.value,
    title,
    position,
  });
}
</script>
